import React, { useCallback, useEffect, useState } from 'react';

import { OuncesLabel } from './ounces-label';
import { PoundsLabel } from './pounds-label';
import { CSWFieldId } from '../../../../constants/field-ids';
import { UnitDisplayType } from '../../../../enums/units/unit-display-type';
import { UnitPosition } from '../../../../enums/units/unit-position';
import { WeightNumberInput } from '../weight-number-input';

/**
 * @typedef {import('./ounces-weight-input-types.d.ts').GetPoundsAndOuncesFromOunces} GetPoundsAndOuncesFromOunces
 * @typedef {import('./ounces-weight-input-types.d.ts').CombinePoundsAndOunces} CombinePoundsAndOunces
 * @typedef {import('./ounces-weight-input-types.d.ts').WeightChangeHandler} WeightChangeHandler
 * @typedef {import('./ounces-weight-input-types.d.ts').TriggerStateChangeHandler} TriggerStateChangeHandler
 * @typedef {import('./ounces-weight-input-types.d.ts').ArrowButtonPressHandler} ArrowButtonPressHandler
 * @typedef {import('./ounces-weight-input-types.d.ts').BlurHandler} BlurHandler
 * @typedef {import('./ounces-weight-input-types.d.ts').KeyDownHandler} KeyDownHandler
 * @typedef {import('./ounces-weight-input-types.d.ts').OuncesWeightInputComponent} OuncesWeightInputComponent
 */

// WARNING: These values are used by smoke tests, so if you change this, update the tests accordingly
const PoundsTestDataInputType = '(lb)';
const OuncesTestDataInputType = '(oz)';

/**
 * @type {GetPoundsAndOuncesFromOunces}
 */
export const getPoundsAndOuncesFromOunces = (value) => {
  const pounds = value || value === 0 ? Math.floor(value / 16) : value;
  const ounces = value || value === 0 ? Math.floor((value % 16) * 100000) / 100000 : value;
  return {
    pounds,
    ounces,
  };
};

/**
 * @type {CombinePoundsAndOunces}
 */
export const combinePoundsAndOunces = (pounds = 0, ounces = 0) => {
  return Math.max(pounds * 16 + ounces, 0);
};

/**
 * @type {OuncesWeightInputComponent}
 */
export const OuncesWeightInput = ({
  inputClassName = null,
  disabled = false,
  shrinkable = false,
  weightValue = null,
  onChange,
  unitDisplayType = UnitDisplayType.AbbreviatedWithParentheses,
  unitPosition = UnitPosition.OutsideEnd,
  numberInputContainerClassName,
  labelId,
}) => {
  const weight = getPoundsAndOuncesFromOunces(weightValue);
  const [poundsWeightValue, setPoundsWeightValue] = useState(weight.pounds);
  const [ouncesWeightValue, setOuncesWeightValue] = useState(weight.ounces);

  useEffect(() => {
    const combinedWeight = getPoundsAndOuncesFromOunces(weightValue);
    setPoundsWeightValue(combinedWeight.pounds);
    setOuncesWeightValue(combinedWeight.ounces);
  }, [weightValue]);

  /**
   * @type {WeightChangeHandler}
   */
  const onOunceChange = useCallback((ounces) => {
    setOuncesWeightValue(ounces);
  }, []);

  /**
   * @type {WeightChangeHandler}
   */
  const onPoundChange = useCallback((pounds) => {
    setPoundsWeightValue(pounds);
  }, []);

  /**
   * @type {TriggerStateChangeHandler}
   */
  const onTriggerStateChange = useCallback(() => {
    const updatedWeight = combinePoundsAndOunces(poundsWeightValue, ouncesWeightValue);
    onChange(updatedWeight);
  }, [poundsWeightValue, ouncesWeightValue, onChange]);

  /**
   * @type {KeyDownHandler}
   */
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        onTriggerStateChange();
      }
    },
    [onTriggerStateChange],
  );

  /**
   * @type {BlurHandler}
   */
  const onBlur = useCallback(() => {
    onTriggerStateChange();
  }, [onTriggerStateChange]);

  /**
   * @type {ArrowButtonPressHandler}
   */
  const onPoundArrowButtonPress = useCallback(
    (newPoundValue) => {
      setPoundsWeightValue(newPoundValue);

      const updatedWeight = combinePoundsAndOunces(newPoundValue, ouncesWeightValue);
      onChange(updatedWeight);
    },
    [ouncesWeightValue, onChange],
  );

  /**
   * @type {ArrowButtonPressHandler}
   */
  const onOunceArrowButtonPress = useCallback(
    (newOunceValue) => {
      setOuncesWeightValue(newOunceValue);

      const updatedWeight = combinePoundsAndOunces(poundsWeightValue, newOunceValue);
      onChange(updatedWeight);
    },
    [poundsWeightValue, onChange],
  );

  return (
    <>
      <WeightNumberInput
        className={inputClassName}
        containerClassName={numberInputContainerClassName}
        disabled={disabled}
        labelId={CSWFieldId.Pounds}
        min={0}
        onArrowButtonPress={onPoundArrowButtonPress}
        onBlur={onBlur}
        onChange={onPoundChange}
        onKeyDown={onKeyDown}
        parentLabelId={labelId}
        shrinkable={shrinkable}
        testDataInputType={PoundsTestDataInputType}
        testId='weight-input-pounds'
        unitPosition={unitPosition}
        value={poundsWeightValue}
        weightUnitLabel={<PoundsLabel unitDisplayType={unitDisplayType} />}
      />
      <WeightNumberInput
        className={inputClassName}
        containerClassName={numberInputContainerClassName}
        disabled={disabled}
        labelId={CSWFieldId.Ounces}
        min={0}
        onArrowButtonPress={onOunceArrowButtonPress}
        onBlur={onBlur}
        onChange={onOunceChange}
        onKeyDown={onKeyDown}
        parentLabelId={labelId}
        shrinkable={shrinkable}
        testDataInputType={OuncesTestDataInputType}
        testId='weight-input-ounces'
        unitPosition={unitPosition}
        value={ouncesWeightValue}
        weightUnitLabel={<OuncesLabel unitDisplayType={unitDisplayType} />}
      />
    </>
  );
};
